import { ColumnsType } from 'antd/es/table';
import { TableComponent } from 'baseComponents/TableComponent';
import {
  tableFilters,
  tableSorters,
} from 'baseComponents/TableComponent/table.utils';
import { TableFilter } from 'baseComponents/TableComponent/TableFilter/TableFilter';
import {
  PurchaseHistory,
  useGetPurchaseHistory,
} from 'hooks/useGetPaymentHistory';
import { ButtonComponent } from 'baseComponents/ButtonComponent';
import { Result, Typography } from 'antd';
import { PurchaseHistoryDetailsModal } from '../PurchaseHistoryDetailsModal';
import dayjs from 'dayjs';
import { useGetPurchaseHistoryRefresh } from 'hooks/useGetPaymentHistoryRefresh';

export const isCreditAdjustment = (entry: PurchaseHistory) =>
  entry.status === 'CREDITS_ADDED' || entry.status === 'CREDITS_REMOVED';

export const purchaseHistoryColumns = (
  admin = false
): ColumnsType<PurchaseHistory> => [
  {
    title: 'Date',
    dataIndex: 'date',
    key: 'date',
    sorter: tableSorters.string('date'),
    defaultSortOrder: 'descend',
    render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    sortDirections: ['descend', 'ascend'],
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (status, record) =>
      isCreditAdjustment(record) ? (
        status === 'CREDITS_ADDED' ? (
          'Credits added'
        ) : (
          'Credits removed'
        )
      ) : admin ? (
        status.toLowerCase().replace(/_/g, ' ')
      ) : (
        <PurchaseHistoryDetailsModal status={status} id={record.id} />
      ),
    sorter: tableSorters.string('status'),
    sortDirections: ['descend', 'ascend'],
    onFilter: tableFilters.includes('status'),
    filterDropdown: (args) => <TableFilter dataIndex='status' {...args} />,
  },
  {
    title: 'Credits',
    dataIndex: 'purchased_credits',
    key: 'credits',
    render: (value, record) =>
      isCreditAdjustment(record) && value > 0
        ? `+${value.toLocaleString('en-GB')}`
        : value?.toLocaleString('en-GB'),
  },
  {
    title: 'Amount (£)',
    dataIndex: 'purchased_amount',
    key: 'amount',
    render: (value, record) =>
      isCreditAdjustment(record) ? '—' : (value / 100).toFixed(2),
  },
  {
    title: 'Adjustment details',
    key: 'adjustment',
    render: (_, record) =>
      isCreditAdjustment(record) ? (
        <div style={{ minWidth: 210, maxWidth: 360, overflowWrap: 'anywhere' }}>
          <div>{record.adjustment_reason}</div>
          <Typography.Text type='secondary'>
            {record.adjusted_by_name} · Balance{' '}
            {record.balance_before?.toLocaleString('en-GB')}
            {' → '}
            {record.balance_after?.toLocaleString('en-GB')}
          </Typography.Text>
        </div>
      ) : (
        '—'
      ),
  },
];
export const PurchaseHistoryTab = () => {
  const {
    isLoading: historyLoading,
    isRefetching: historyRefetching,
    error,
    isError,
    data: history = [],
  } = useGetPurchaseHistory();
  const { mutate: refresh, isPending: refreshIsPending } =
    useGetPurchaseHistoryRefresh();

  if (isError) {
    return (
      <Result
        title={error.response?.data?.message || 'Error fetching history'}
      />
    );
  }

  return (
    <TableComponent
      loading={historyLoading || historyRefetching}
      columns={purchaseHistoryColumns()}
      total={history.length}
      dataSource={history}
      rowKey='id'
      ctaButtons={
        <>
          <ButtonComponent
            label='Refresh'
            appereance='secondary'
            size='middle'
            style={{ maxWidth: 320 }}
            disabled={historyLoading || historyRefetching || refreshIsPending}
            onClick={() => {
              refresh();
            }}
          />
        </>
      }
    />
  );
};
